import { List, OrderedMap } from 'immutable';

import {
  BalanceFactory,
  PaymentApprovalsFactory,
  PaymentFactory,
  PaymentInfoFactory,
  PaymentInvoicesListFactory,
  PaymentTagsFactory,
  RejectReasonFactory,
} from '@/domain/payments/factories';
import type { PaymentRecord, PaymentsOrderedMap, PaymentApprovalsRecord, PaymentInfoData, PaymentInvoiceData, BalanceData, PaymentMeta } from '@/domain/payments/types';
import { PaymentBeneficiarySchema } from '@/domain/payments/schemas';
import { AdvancedApprovalsGroupLevelsAdapter } from '@/domain/approvals';

// The payment goes through the legacy client, which validates nothing at the
// boundary, so every `PaymentInfoFactory(data.info)` call site routes via here.
const parsePaymentBeneficiary = (payTo: unknown) => {
  const result = PaymentBeneficiarySchema.safeParse(payTo);
  if (result.success) return result.data;
  // Logged before rethrowing: a ZodError has no `err.response`, so the toast helper
  // drops it and the page would render empty with no signal. Keys only — the value
  // carries account details.
  console.error('[payTo] unexpected beneficiary shape on the payment', result.error.issues, {
    keys: payTo && typeof payTo === 'object' ? Object.keys(payTo) : typeof payTo,
  });
  throw result.error;
};

export const PaymentInfoAdapter = (info: Partial<PaymentInfoData> = {}) =>
  PaymentInfoFactory({
    ...info,
    payTo: info.payTo == null ? null : parsePaymentBeneficiary(info.payTo),
  });

interface PaymentApprovalsApiResponse {
  levels?: unknown[];
  created_at?: string;
  updated_at?: string;
  template_id?: string;
  next_level_id?: string;
  rejected_reasons?: Array<{ username: string; message: string }>;
  [key: string]: unknown;
}

interface PaymentApiResponse {
  info: PaymentInfoData;
  invoices: PaymentInvoiceData[];
  approvals: PaymentApprovalsApiResponse;
  balance: BalanceData | null;
  meta?: PaymentMeta;
  tags?: string[];
}

export const PaymentsListAdapter = (payments: Array<{ id: string }>): PaymentsOrderedMap =>
  OrderedMap(payments.map((payment) => [payment.id, payment as unknown as PaymentRecord]));

const RejectReasonsAdapter = (rejectReasons: Array<{ username: string; message: string }> = []) =>
  List(rejectReasons).map(RejectReasonFactory);

export const PaymentApprovalsAdapter = ({
  levels = [],
  created_at: createdAt = '',
  updated_at: updatedAt = '',
  template_id: templateId = '',
  next_level_id: nextLevelId = '',
  rejected_reasons: rejectReasons = [],
  ...rest
}: PaymentApprovalsApiResponse): PaymentApprovalsRecord =>
  PaymentApprovalsFactory({
    levels: AdvancedApprovalsGroupLevelsAdapter(levels as never[]), // cast: TS infers never[] for default [] param in JS source
    createdAt,
    updatedAt,
    templateId,
    nextLevelId,
    rejectReasons: RejectReasonsAdapter(rejectReasons),
    ...rest,
  });

export const PaymentAdapter = (payments: PaymentApiResponse): PaymentRecord =>
  PaymentFactory({
    info: PaymentInfoAdapter(payments.info),
    invoices: PaymentInvoicesListFactory(payments.invoices),
    approvals: PaymentApprovalsAdapter(payments.approvals),
    balance: BalanceFactory(payments.balance || {}),
    meta: payments.meta || {},
    tags: PaymentTagsFactory(payments.tags),
  });
